"use client";

import React, { useState } from "react";
import Modal from "Common/Components/Modal";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Plus } from "lucide-react";
import { useAppServices } from "hook/services";
import { useParams } from "react-router-dom";

interface propTypes {
  handleRefresh: () => void;
}

const NoteCreateModal = ({ handleRefresh }: propTypes) => {
  const AppService = useAppServices();
  const { locationId, contactId } = useParams(); // ✅ get from params

  const [show, setShow] = useState<boolean>(false);
  const toggle = () => setShow((prev) => !prev);

  const handleSubmit = async (values: any) => {
    const payload = {
      ...values,
      locationId,
      contactId,
    };

    const { response } = await AppService.note.create({
      payload,
      toaster: true,
    });

    if (response) {
      formik.resetForm();
      toggle();
      handleRefresh();
    }
  };

  // ✅ Formik setup
  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
    },
    validationSchema: Yup.object({
      title: Yup.string()
        .trim()
        .min(2, "Title must be at least 2 characters long")
        .max(100, "Title cannot exceed 100 characters")
        .required("Please enter note title"),
      description: Yup.string()
        .trim()
        .max(500, "Description cannot exceed 500 characters")
        .optional(),
    }),
    onSubmit: (values) => handleSubmit(values),
  });

  return (
    <>
      {/* Add Note Button */}
      <button
        type="button"
        className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600 focus:ring focus:ring-custom-100 dark:focus:ring-custom-400/20"
        onClick={toggle}
      >
        <Plus className="inline-block size-4 mr-1" />
        <span className="align-middle">Add Note</span>
      </button>

      {/* Modal */}
      <Modal
        show={show}
        onHide={toggle}
        id="noteModal"
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[30rem] bg-white dark:bg-zink-700 shadow rounded-md dark:shadow-zink-900/50"
      >
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-500/20">
          <Modal.Title className="text-16 font-semibold text-zink-800 dark:text-zink-100">
            Add New Note
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            {/* Title */}
            <div>
              <label className="block mb-1 text-sm font-medium text-zink-800 dark:text-zink-100">
                Title
              </label>
              <input
                type="text"
                name="title"
                placeholder="Enter note title"
                className="form-input w-full dark:bg-zink-600 dark:border-zink-500 dark:text-zink-100 dark:placeholder:text-zink-400"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.title}
              />
              {formik.touched.title && formik.errors.title && (
                <p className="text-red-400 dark:text-red-300 text-sm mt-1">
                  {formik.errors.title}
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block mb-1 text-sm font-medium text-zink-800 dark:text-zink-100">
                Description
              </label>
              <textarea
                name="description"
                placeholder="Enter note description (optional)"
                className="form-textarea w-full h-24 dark:bg-zink-600 dark:border-zink-500 dark:text-zink-100 dark:placeholder:text-zink-400"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.description}
              />
              {formik.touched.description && formik.errors.description && (
                <p className="text-red-400 dark:text-red-300 text-sm mt-1">
                  {formik.errors.description}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="text-red-500 dark:text-red-400 bg-white dark:bg-zink-600 border border-slate-200 dark:border-zink-500 btn hover:text-red-600 dark:hover:text-red-300 hover:bg-slate-50 dark:hover:bg-zink-500"
                onClick={() => {
                  formik.resetForm();
                  toggle();
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600 dark:bg-custom-500 dark:border-custom-500 dark:hover:bg-custom-600"
              >
                Add Note
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default NoteCreateModal;
